/**
 * Checking in and out, asking HR, and the messages sent — the shapes, and the
 * distance arithmetic the demonstration uses. The platform's own is
 * `backend/api/slt/hr/clock.py`; change both.
 *
 * Three ways in: the person's phone inside a designated location, a face
 * reader, a fingerprint reader. Readers send their own feed; HR may record
 * one by hand, and says why.
 */

export type ClockMethod =
  | "phone_location"
  | "phone_biometric"
  | "phone_face"
  | "phone_fingerprint"
  | "terminal_face"
  | "terminal_fingerprint"
  | "manual";

export const METHOD: Record<ClockMethod, { label: string; icon: string }> = {
  phone_location: { label: "Phone, by location", icon: "📍" },
  phone_biometric: { label: "Phone, location and face or fingerprint", icon: "🔐" },
  phone_face: { label: "Phone face check", icon: "🙂" },
  phone_fingerprint: { label: "Phone fingerprint", icon: "👆" },
  terminal_face: { label: "Face recognition reader", icon: "🙂" },
  terminal_fingerprint: { label: "Fingerprint reader", icon: "👆" },
  manual: { label: "Recorded by HR", icon: "✍" },
};

/** The three ways HR may allow somebody to check in, per person. */
export type PolicyMethod = "phone_location" | "terminal_face" | "terminal_fingerprint";

export const POLICY_METHODS: { key: PolicyMethod; label: string; icon: string; when: string }[] = [
  {
    key: "terminal_fingerprint",
    label: "Fingerprint reader",
    icon: "👆",
    when: "Restricted and airside areas, where the person must be proven at the gate.",
  },
  {
    key: "terminal_face",
    label: "Face recognition reader",
    icon: "🙂",
    when: "Buildings with a reader at the entrance: terminal, offices, plant rooms.",
  },
  {
    key: "phone_location",
    label: "Phone, by location",
    icon: "📍",
    when: "Field staff who start at different places, or where there is no reader.",
  },
];

/** Suggested reasons HR can pick from, so the file says why. */
export const POLICY_BASES = [
  "Works airside; restricted area — fingerprint reader only",
  "Office-based; the building has a face reader",
  "Field staff across the airfield; checks in by phone at the location",
  "Shift work in the terminal; face reader at the staff entrance",
  "Contractor staff; fingerprint reader at the contractor gate",
];

export interface HrGeofence {
  id: string;
  name: string;
  lat: number;
  lon: number;
  radiusM: number;
  shiftStart: string;
  graceMin: number;
  active?: boolean;
  siteId?: string | null;
}

export interface HrClockEvent {
  id: string;
  employeeId: string;
  name: string;
  employeeNo: string;
  department: string | null;
  direction: "in" | "out";
  at: string;
  method: ClockMethod;
  lat: number | null;
  lon: number | null;
  accuracyM: number | null;
  distanceM: number | null;
  inside: boolean | null;
  verified: boolean;
  device: string | null;
  note: string | null;
  place: string | null;
}

export type NotifyStatus = "queued" | "sent" | "failed" | "no_address" | "not_configured";
export type NotifyEvent =
  | "received"
  | "approved"
  | "rejected"
  | "issued"
  | "closed"
  | "cancelled"
  | "status_changed"
  | "confidential"
  | "clock_changed";

export const NOTIFY_STATUS: Record<NotifyStatus, { label: string; colour: string }> = {
  queued: { label: "Waiting to send", colour: "var(--blue)" },
  sent: { label: "Sent", colour: "var(--green)" },
  failed: { label: "Failed", colour: "var(--red)" },
  no_address: { label: "No number or email on file", colour: "var(--amber)" },
  not_configured: { label: "Not sent — no provider set up", colour: "var(--amber)" },
};

export const NOTIFY_EVENT: Record<NotifyEvent, string> = {
  received: "Request received",
  approved: "Approved",
  rejected: "Not approved",
  issued: "Letter issued",
  closed: "Closed",
  cancelled: "Canceled",
  status_changed: "Employment status changed",
  confidential: "Confidential record added",
  clock_changed: "Check-in method changed",
};

export interface HrNotification {
  id: string;
  employeeId: string;
  name: string;
  employeeNo: string;
  recordId: string | null;
  channel: "sms" | "email";
  toAddress: string | null;
  subject: string | null;
  body: string;
  event: NotifyEvent;
  status: NotifyStatus;
  attempts: number;
  error: string | null;
  createdAt: string;
  sentAt: string | null;
}

export interface HrChannels {
  email: { configured: boolean; provider: string | null; from: string | null };
  sms: { configured: boolean; provider: string | null; sender: string | null };
}

/** What somebody may ask HR for, on the phone or the console. */
export const REQUEST_TYPES: { key: string; label: string; kind: "leave" | "letter" | "request" }[] = [
  { key: "annual", label: "Annual leave", kind: "leave" },
  { key: "sick", label: "Sick leave", kind: "leave" },
  { key: "hajj", label: "Hajj leave", kind: "leave" },
  { key: "marriage", label: "Marriage leave", kind: "leave" },
  { key: "paternity", label: "Birth of a child", kind: "leave" },
  { key: "bereavement", label: "Bereavement", kind: "leave" },
  { key: "exam", label: "Exam leave", kind: "leave" },
  { key: "unpaid", label: "Unpaid leave", kind: "leave" },
  { key: "letter", label: "A letter (employment, salary, NOC…)", kind: "letter" },
  { key: "status_change", label: "A change to my status or shift", kind: "request" },
  { key: "details_change", label: "A change to my personal details", kind: "request" },
  { key: "salary_advance", label: "A salary advance", kind: "request" },
  { key: "loan", label: "A loan", kind: "request" },
  { key: "transfer", label: "A transfer", kind: "request" },
  { key: "training", label: "Training", kind: "request" },
  { key: "resignation", label: "Resignation", kind: "request" },
  { key: "other", label: "Something else", kind: "request" },
];
export const REQUEST_BY = new Map(REQUEST_TYPES.map((r) => [r.key, r]));

export interface HrSelfRequest {
  type: string;
  title: string;
  details?: string | null;
  startsOn?: string | null;
  endsOn?: string | null;
  letterType?: string | null;
}

export interface HrMe {
  employee: { id: string; employeeNo: string; name: string; department: string | null; jobTitle: string | null };
  hasPhone: boolean;
  hasEmail: boolean;
  /** how HR has set this person to check in; null = not decided */
  clockMethods: PolicyMethod[] | null;
  clockBasis: string | null;
  phoneAllowed: boolean;
  today: { status: string; checkIn: string | null; checkOut: string | null; minutesLate: number | null } | null;
  events: { direction: "in" | "out"; at: string; method: ClockMethod; inside: boolean | null; distanceM: number | null; place: string | null }[];
  requests: {
    id: string;
    kind: string;
    status: string;
    title: string;
    occurredOn: string;
    startsOn: string | null;
    endsOn: string | null;
    reference: string | null;
    decisionNote: string | null;
    decidedAt: string | null;
  }[];
  locations: HrGeofence[];
}

export interface HrClockInput {
  direction: "in" | "out";
  lat: number;
  lon: number;
  accuracyM: number | null;
  clientId: string;
  device?: string;
}

/* ---- where somebody is ---------------------------------------------------------- */

export function distanceM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const r = 6_371_000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dp = rad(lat2 - lat1);
  const dl = rad(lon2 - lon1);
  const a = Math.sin(dp / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dl / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(a));
}

export interface Located {
  fence: HrGeofence | null;
  distanceM: number | null;
  inside: boolean;
  reason: string | null;
}

/** The same decision the platform makes: nearest location, inside or not, and why not. */
export function locate(fences: HrGeofence[], lat: number, lon: number, accuracy: number | null): Located {
  let fence: HrGeofence | null = null;
  let best: number | null = null;
  for (const f of fences) {
    const d = distanceM(lat, lon, f.lat, f.lon);
    if (best == null || d < best) {
      best = d;
      fence = f;
    }
  }
  if (!fence || best == null) return { fence: null, distanceM: null, inside: false, reason: "no check-in location has been set up" };
  if ((accuracy ?? 0) > Math.max(100, fence.radiusM))
    return {
      fence,
      distanceM: Math.round(best),
      inside: false,
      reason: `the phone's location is only good to ${Math.round(accuracy ?? 0)} m; move into the open and try again`,
    };
  const inside = best <= fence.radiusM;
  return {
    fence,
    distanceM: Math.round(best * 10) / 10,
    inside,
    reason: inside ? null : `${Math.round(best)} m from ${fence.name}; come within ${fence.radiusM} m`,
  };
}

/** Present or late, against the location's shift start and grace. */
export function dayStatus(time: string, shiftStart: string, graceMin: number): { status: "present" | "late"; minutesLate: number } {
  const mins = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
  const late = mins(time) - mins(shiftStart);
  return late > graceMin ? { status: "late", minutesLate: late } : { status: "present", minutesLate: 0 };
}
